import React, { useState, useEffect } from "react";
import { NavLink } from "react-router-dom";
import { FaBars, FaTimes, FaChevronDown } from "react-icons/fa";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import logo from "../assets/rising-star-logo-1.jpg";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../config/firebaseConfig";
import { getDashboardConfig } from "../utils/roleAccess";

import Avatar from "../assets/avatar.jpg";

function Navbar() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMoreOpen, setIsMoreOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [dashboardConfig, setDashboardConfig] = useState(null);
  const auth = getAuth();

  const toggleMenu = () => setIsMenuOpen((prev) => !prev);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        console.log("User logged in:", currentUser.uid);
        setUser(currentUser);

        try {
          // Fetch user details from Firestore
          const userRef = doc(db, "users", currentUser.uid);
          const userSnap = await getDoc(userRef);

          if (userSnap.exists()) {
            const userData = userSnap.data();
            console.log("Firestore user data:", userData);

            // Check both cases: single "role" string OR "roles" array
            setDashboardConfig(getDashboardConfig(userData));
          } else {
            setDashboardConfig(null);
          }
        } catch (err) {
          console.error("Error fetching user data:", err);
          setDashboardConfig(null);
        }
      } else {
        console.log("No user logged in");
        setUser(null);
        setDashboardConfig(null);
      }
    });

    return () => unsubscribe();
  }, [auth]);

  const navItems = [
    { to: "/", label: "Home" },
    { to: "/about", label: "About Us" },
    { to: "/academics", label: "Academics" },
    { to: "/admissions", label: "Admissions" },
    { to: "/news-and-events", label: "News & Events" },
    { to: "/vacancies", label: "Vacancies" },
    { to: "/contact", label: "Contact Us" },
  ];

  return (
    <nav className="bg-gradient-to-r from-[#0b2342] via-[#10345f] to-[#0b2342] text-white shadow-lg fixed w-full top-0 left-0 z-50 border-b border-white/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16 md:h-[72px] gap-4">
        {/* Logo */}
        <a href="/" className="flex items-center gap-3 min-w-0 flex-1">
          <img
            src={logo}
            alt="Logo"
            className="h-10 w-10 rounded-full shadow-sm ring-2 ring-white/20"
          />
          <span className="text-sm sm:text-lg md:text-xl font-semibold tracking-wide leading-tight whitespace-normal">
            Rising Stars Public School
          </span>
        </a>

        {/* Desktop Nav */}
        <ul className="hidden md:flex items-center gap-2 lg:gap-3 text-sm font-medium">
          {navItems.map((item) => (
            <li key={item.to}>
              <NavLink
                to={item.to}
                className={({ isActive }) =>
                  `inline-flex items-center gap-2 px-3 py-2 rounded-full transition-all duration-200 hover:bg-white/10 hover:text-yellow-300 whitespace-nowrap ${
                    isActive
                      ? "text-yellow-300 bg-white/10 shadow-sm"
                      : "text-white/90"
                  }`
                }
              >
                {item.label}
              </NavLink>
            </li>
          ))}
          <li className="group relative">
            <button
              type="button"
              className="inline-flex items-center gap-2 rounded-full px-3 py-2 text-white/90 transition-all duration-200 hover:bg-white/10 hover:text-yellow-300"
              aria-haspopup="true"
              aria-label="Open more navigation options"
            >
              More
              <FaChevronDown className="text-[10px]" />
            </button>
            <ul className="invisible absolute right-0 top-full mt-2 w-64 translate-y-1 rounded-xl border border-white/10 bg-[#0b2342] p-2 opacity-0 shadow-xl transition-all duration-200 group-hover:visible group-hover:translate-y-0 group-hover:opacity-100 group-focus-within:visible group-focus-within:translate-y-0 group-focus-within:opacity-100">
              <li>
                <NavLink
                  to="/mandatory-public-disclosure"
                  className={({ isActive }) =>
                    `block whitespace-nowrap rounded-lg px-3 py-2 text-sm transition hover:bg-white/10 hover:text-yellow-300 ${
                      isActive ? "text-yellow-300" : "text-white/85"
                    }`
                  }
                >
                  Mandatory Public Disclosure
                </NavLink>
              </li>
            </ul>
          </li>
          {dashboardConfig?.path && (
            <li>
              <NavLink
                to={dashboardConfig.path}
                className="ml-1 inline-flex items-center justify-center rounded-full bg-emerald-500/90 hover:bg-emerald-500 text-white px-3 py-2 text-sm font-semibold transition whitespace-nowrap"
              >
                {dashboardConfig.label}
              </NavLink>
            </li>
          )}
          {user && (
            <li>
              <NavLink
                to="/profile"
                title="Profile"
                className="flex items-center"
              >
                <div className="w-9 h-9 rounded-full overflow-hidden border-2 border-white/70 shadow-sm cursor-pointer">
                  <img
                    src={Avatar}
                    alt="profile"
                    className="w-full h-full object-cover"
                  />
                </div>
              </NavLink>
            </li>
          )}
        </ul>

        {/* Hamburger Icon */}
        <button
          type="button"
          className="md:hidden inline-flex items-center justify-center w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 transition"
          onClick={toggleMenu}
          aria-label="Toggle navigation menu"
          aria-expanded={isMenuOpen}
        >
          <span className="text-xl text-white">
            {isMenuOpen ? <FaTimes /> : <FaBars />}
          </span>
        </button>
      </div>

      {/* Mobile Menu */}
      <div
        className={`md:hidden bg-[#0b2342] transition-all duration-300 ease-in-out border-t border-white/10 ${
          isMenuOpen ? "max-h-[80vh] py-4" : "max-h-0 overflow-hidden"
        }`}
      >
        <ul className="flex flex-col space-y-2 px-4 sm:px-6">
          {navItems.map((item) => (
            <li key={item.to}>
              <NavLink
                to={item.to}
                className={({ isActive }) =>
                  `block py-2.5 px-3 rounded-lg transition whitespace-nowrap ${
                    isActive
                      ? "bg-white/10 text-yellow-300 font-semibold"
                      : "hover:bg-white/10"
                  }`
                }
                onClick={toggleMenu}
              >
                {item.label}
              </NavLink>
            </li>
          ))}
          <li>
            <button
              type="button"
              className="flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-left transition hover:bg-white/10 hover:text-yellow-300"
              onClick={() => setIsMoreOpen((prev) => !prev)}
              aria-expanded={isMoreOpen}
            >
              <span>More</span>
              <FaChevronDown
                className={`text-xs transition-transform ${
                  isMoreOpen ? "rotate-180" : ""
                }`}
              />
            </button>
            {isMoreOpen && (
              <ul className="ml-3 mt-1 border-l border-white/20 pl-3">
                <li>
                  <NavLink
                    to="/mandatory-public-disclosure"
                    className="block whitespace-nowrap rounded-lg px-3 py-2.5 text-sm text-white/80 transition hover:bg-white/10 hover:text-yellow-300"
                    onClick={toggleMenu}
                  >
                    Mandatory Public Disclosure
                  </NavLink>
                </li>
              </ul>
            )}
          </li>
          {user && (
            <li>
              <NavLink
                to="/profile"
                title="Profile"
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-white/10 transition whitespace-nowrap"
                onClick={toggleMenu}
              >
                <div className="w-9 h-9 rounded-full overflow-hidden border-2 border-white/70 shadow-sm">
                  <img
                    src={Avatar}
                    alt="profile"
                    className="w-full h-full object-cover"
                  />
                </div>
                <span className="text-sm font-medium">Profile</span>
              </NavLink>
            </li>
          )}

          {dashboardConfig?.path && (
            <li>
              <NavLink
                to={dashboardConfig.path}
                onClick={toggleMenu}
                className="w-full text-left py-2.5 px-3 rounded-lg bg-emerald-500/90 hover:bg-emerald-500 text-white transition font-semibold whitespace-nowrap"
              >
                {dashboardConfig.label}
              </NavLink>
            </li>
          )}
        </ul>
      </div>
    </nav>
  );
}

export default Navbar;
